import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { supabase } from './supabaseClient';
import tracker from './activityTracker';

const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
const PENDING_REFERRAL_STORAGE_KEY = 'pendingReferralCode';

const normalizeReferralCode = (code = '') => code.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 24);

const getStoredReferralCode = () => {
  if (typeof window === 'undefined') return '';
  return normalizeReferralCode(window.localStorage.getItem(PENDING_REFERRAL_STORAGE_KEY) || '');
};

const setStoredReferralCode = (code) => {
  if (typeof window === 'undefined') return '';
  const normalized = normalizeReferralCode(code);
  if (normalized) {
    window.localStorage.setItem(PENDING_REFERRAL_STORAGE_KEY, normalized);
  } else {
    window.localStorage.removeItem(PENDING_REFERRAL_STORAGE_KEY);
  }
  return normalized;
};

const clearStoredReferralCode = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(PENDING_REFERRAL_STORAGE_KEY);
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingReferralCode, setPendingReferralCode] = useState(() => getStoredReferralCode());
  const loginTrackedRef = useRef(null);
  const referralApplyRef = useRef(null);

  const captureReferralCodeFromUrl = useCallback(() => {
    if (typeof window === 'undefined') return '';
    const params = new URLSearchParams(window.location.search);
    const code = normalizeReferralCode(params.get('ref') || '');
    if (!code) {
      const stored = getStoredReferralCode();
      setPendingReferralCode(stored);
      return stored;
    }

    const stored = setStoredReferralCode(code);
    setPendingReferralCode(stored);

    params.delete('ref');
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, '', nextUrl);
    return stored;
  }, []);

  const applyPendingReferralCode = useCallback(async () => {
    const storedCode = getStoredReferralCode();
    if (!storedCode) {
      setPendingReferralCode('');
      return { applied: false, skipped: true, reason: 'missing_code' };
    }

    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session?.access_token) {
      return { applied: false, skipped: true, reason: 'missing_session' };
    }

    const formData = new FormData();
    formData.append('referral_code', storedCode);
    const response = await axios.post(`${API_URL}/referrals/apply`, formData, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    const result = response.data || {};
    if (result.applied || ['already_applied', 'claim_window_expired', 'invalid_code', 'self_referral'].includes(result.reason)) {
      clearStoredReferralCode();
      setPendingReferralCode('');
    }

    return result;
  }, []);

  const checkAuth = async () => {
    try {
      // Get the current session
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error getting session:', error);
        setUser(null);
      } else if (session) {
        // Check if user's email is confirmed
        if (session.user.email_confirmed_at) {
          setUser(session.user);
        } else {
          // User exists but email not confirmed - sign them out
          await supabase.auth.signOut();
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    captureReferralCodeFromUrl();
    checkAuth();

    // Handle OAuth callback - Supabase automatically processes URL fragments
    // Check if we're returning from an OAuth provider
    const handleOAuthCallback = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (session && !error) {
          // OAuth callback successful - session is already established
          if (session.user.email_confirmed_at) {
            setUser(session.user);
          }
        }
      } catch (error) {
        console.error('Error handling OAuth callback:', error);
      }
    };

    // Check for OAuth callback on mount
    handleOAuthCallback();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        
        // Handle OAuth callback events
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          if (session && session.user.email_confirmed_at) {
            setUser(session.user);
            setLoading(false);
            tracker.setAuthToken(session.access_token);

            if (event === 'SIGNED_IN' && loginTrackedRef.current !== session.user.id) {
              loginTrackedRef.current = session.user.id;
              const provider = session.user.app_metadata?.provider || 'email';
              const formData = new FormData();
              formData.append('login_method', provider);
              axios.post(`${API_URL}/auth/track-login`, formData, {
                headers: { Authorization: `Bearer ${session.access_token}` },
              }).catch(() => {});
            }
          }
        } else if (event === 'SIGNED_OUT') {
          loginTrackedRef.current = null;
          tracker.setAuthToken(null);
          setUser(null);
          setLoading(false);
        }
        
        // Only update state if there's an actual change
        if (session) {
          // Check if user's email is confirmed
          if (session.user.email_confirmed_at) {
            // Always update user state with the latest session user
            setUser(prev => {
              // Only update if different to prevent unnecessary re-renders
              if (!prev || prev.id !== session.user.id || JSON.stringify(prev) !== JSON.stringify(session.user)) {
                return session.user;
              }
              return prev;
            });
          } else {
            // User exists but email not confirmed - don't set as authenticated
            setUser(prev => prev ? null : prev);
          }
        } else {
          // Only clear user if there is one
          setUser(prev => prev ? null : prev);
        }
        
        // Only update loading if it's currently true
        setLoading(prev => prev ? false : prev);
      }
    );

    return () => subscription.unsubscribe();
  }, [captureReferralCodeFromUrl]); // Empty behavior retained via stable callback

  useEffect(() => {
    const syncPendingReferral = async () => {
      if (!user?.id) return;
      const storedCode = getStoredReferralCode();
      if (!storedCode) return;

      const attemptKey = `${user.id}:${storedCode}`;
      if (referralApplyRef.current === attemptKey) return;
      referralApplyRef.current = attemptKey;

      try {
        const result = await applyPendingReferralCode();
        if (!result?.applied && !['already_applied', 'claim_window_expired', 'invalid_code', 'self_referral', 'missing_code'].includes(result?.reason)) {
          referralApplyRef.current = null;
        }
      } catch (error) {
        console.error('Error applying pending referral code:', error);
        referralApplyRef.current = null;
      }
    };

    syncPendingReferral();
  }, [user, applyPendingReferralCode]);

  // Register user with email confirmation
  const register = useCallback(async (email, password, username) => {
    const maxRetries = 3;
    let attempt = 0;
    
    while (attempt < maxRetries) {
      try {
        attempt++;
        
        // Create the user with Supabase auth (email confirmation required)
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              username: username,
            },
            emailRedirectTo: `${window.location.origin}/auth/callback`
          }
        });

        if (error) {
          console.error('AuthContext: Registration error:', error);
          
          // Check if this is a retryable error
          if (error.message.includes('AuthRetryableFetchError') || 
              error.message.includes('fetch') || 
              error.message.includes('timeout') ||
              error.message.includes('504')) {
            
            if (attempt < maxRetries) {
              const delay = Math.pow(2, attempt) * 1000; // Exponential backoff: 2s, 4s, 8s
              console.log(`AuthContext: Retryable error detected, retrying in ${delay}ms...`);
              await new Promise(resolve => setTimeout(resolve, delay));
              continue; // Try again
            } else {
              return { 
                success: false, 
                error: 'Registration failed after multiple attempts. Supabase may be experiencing high load. Please try again in a few minutes.' 
              };
            }
          }
          
          // Non-retryable errors
          let userFriendlyError = error.message;
          
          if (error.message.includes('rate limit') || error.message.includes('too many requests')) {
            userFriendlyError = 'Too many requests - please wait a few minutes before trying again.';
          } else if (error.message.includes('invalid email')) {
            userFriendlyError = 'Please enter a valid email address.';
          } else if (error.message.includes('password')) {
            userFriendlyError = 'Password must be at least 6 characters long.';
          }
          
          return { 
            success: false, 
            error: userFriendlyError 
          };
        }

        if (data.user) {
          return { 
            success: true, 
            message: pendingReferralCode
              ? 'Account created successfully! Please check your email and click the confirmation link to activate your account. Your referral will be applied after your first sign-in.'
              : 'Account created successfully! Please check your email and click the confirmation link to activate your account.',
            requiresConfirmation: true,
            user: data.user
          };
        }
        return { success: false, error: 'Registration failed - no response from server' };
        
      } catch (error) {
        console.error('AuthContext: Registration exception:', error);
        
        if (attempt < maxRetries) {
          const delay = Math.pow(2, attempt) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        let userFriendlyError = 'Registration failed after multiple attempts. Please try again later.';
        
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          userFriendlyError = 'Network error - please check your internet connection and try again.';
        } else if (error.message && error.message.includes('timeout')) {
          userFriendlyError = 'Request timed out - Supabase may be experiencing high load. Please try again in a few minutes.';
        } else if (error.message) {
          userFriendlyError = error.message;
        }
        
        return { 
          success: false, 
          error: userFriendlyError 
        };
      }
    }
    
    return { 
      success: false, 
      error: 'Registration failed after maximum retry attempts. Please try again later.' 
    };
  }, [pendingReferralCode]);



  // Login function with retry logic
  const login = useCallback(async (email, password) => {
    const maxRetries = 3;
    let attempt = 0;
    
    while (attempt < maxRetries) {
      try {
        attempt++;
        
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          // Check if this is a retryable error
          if (error.message.includes('AuthRetryableFetchError') || 
              error.message.includes('fetch') || 
              error.message.includes('timeout') ||
              error.message.includes('504')) {
            
            if (attempt < maxRetries) {
              const delay = Math.pow(2, attempt) * 1000;
              await new Promise(resolve => setTimeout(resolve, delay));
              continue;
            } else {
              return { 
                success: false, 
                error: 'Login failed after multiple attempts. Supabase may be experiencing high load. Please try again in a few minutes.' 
              };
            }
          }
          
          return { 
            success: false, 
            error: error.message 
          };
        }

        if (data.user) {
          // Check if user's email is confirmed
          if (data.user.email_confirmed_at) {
            setUser(data.user);
            return { success: true };
          } else {
            // Sign out user if email not confirmed
            await supabase.auth.signOut();
            return { 
              success: false, 
              error: 'Please confirm your email address before logging in. Check your email for a confirmation link.' 
            };
          }
        }

        return { success: false, error: 'Login failed' };
        
      } catch (error) {
        if (attempt < maxRetries) {
          const delay = Math.pow(2, attempt) * 1000;
          console.log(`AuthContext: Login exception occurred, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        return { 
          success: false, 
          error: error.message || 'Login failed after multiple attempts' 
        };
      }
    }
    
    return { 
      success: false, 
      error: 'Login failed after maximum retry attempts. Please try again later.' 
    };
  }, []);



  // Google OAuth sign-in function
  const signInWithGoogle = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        console.error('Error signing in with Google:', error);
        return { 
          success: false, 
          error: error.message || 'Failed to sign in with Google' 
        };
      }

      // OAuth redirects to Google, so we return success
      // The actual authentication will be handled by the callback
      return { success: true, message: 'Redirecting to Google...' };
    } catch (error) {
      console.error('Google sign-in exception:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to sign in with Google' 
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
      }
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, []);

  const getAuthHeaders = useCallback(async () => {
    try {
      // Always get the current session from Supabase, don't rely on local user state
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error getting session for auth headers:', error);
        return {};
      }
      
      console.log('=== getAuthHeaders Debug ===');
      console.log('Session exists:', !!session);
      console.log('Session user ID:', session?.user?.id);
      console.log('Access token exists:', !!session?.access_token);
      console.log('Token length:', session?.access_token?.length);
      console.log('Token preview:', session?.access_token?.substring(0, 50) + '...');
      
      if (session?.access_token) {
        const headers = { Authorization: `Bearer ${session.access_token}` };
        console.log('Generated headers:', headers);
        return headers;
      } else {
        console.log('No access token found in session');
        // Try to refresh the session
        console.log('Attempting to refresh session...');
        const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError) {
          console.error('Error refreshing session:', refreshError);
          return {};
        }
        
        if (refreshedSession?.access_token) {
          console.log('Session refreshed successfully');
          const headers = { Authorization: `Bearer ${refreshedSession.access_token}` };
          console.log('Generated headers from refreshed session:', headers);
          return headers;
        } else {
          console.log('No access token after refresh');
          return {};
        }
      }
    } catch (error) {
      console.error('Error in getAuthHeaders:', error);
      return {};
    }
  }, []);

  const getDisplayName = useCallback(() => {
    if (!user) return 'User';
    return user.user_metadata?.username || user.email || 'User';
  }, [user]);

  const getDisplayInitial = useCallback(() => {
    if (!user) return 'U';
    const name = user.user_metadata?.username || user.email || 'U';
    return name.charAt(0).toUpperCase();
  }, [user]);

  // Function to verify email confirmation status
  const verifyEmailConfirmation = useCallback(async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error getting session for email verification:', error);
        return { confirmed: false, error: 'Failed to verify session' };
      }
      
      if (!session) {
        return { confirmed: false, error: 'No active session' };
      }
      
      if (session.user.email_confirmed_at) {
        return { confirmed: true, user: session.user };
      } else {
        return { confirmed: false, error: 'Email not confirmed' };
      }
    } catch (error) {
      console.error('Error in verifyEmailConfirmation:', error);
      return { confirmed: false, error: 'Verification failed' };
    }
  }, []);

  const value = {
    user,
    loading,
    login,
    register,
    signInWithGoogle,
    logout,
    getAuthHeaders,
    getDisplayName,
    getDisplayInitial,
    verifyEmailConfirmation,
    pendingReferralCode,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 